import { TGetItemFn } from '@/config/types'
import { WeightParameters } from '@/enums/bodyParameters'
import { PageName } from '@/enums/pageName'

export const BMI_REFERENCE_VALUE = 18.5
export const BMI_REFERENCE_VALUE_IMPERIAL = 25

const ERROR_MESSAGE =
  'Organic Fit is not currently designed for BMIs below 18.5, following World Health Organization (WHO) guidelines that classify BMIs below 18.5 as underweight.'

export const getWeightFormat = (unit: string) => {
  return unit === 'kg' ? 'metric' : 'imperial'
}

export const getBMI = (
  getItem: TGetItemFn,
  value?: string,
  measurementSystem?: string
): number | undefined => {
  const heightDataRaw = getItem(PageName.height) || ''
  const heightData = (heightDataRaw && JSON.parse(heightDataRaw)) || ''

  const weightDataRaw = getItem(PageName.weight) || ''
  const weightData = (weightDataRaw && JSON.parse(weightDataRaw)) || ''

  if (!heightData || (!weightData && !value)) {
    return
  }

  const heightMeasurementSystem = heightData.type
  const weightMeasurementSystem =
    (measurementSystem || weightData.measurementSystem) === WeightParameters.LBS
      ? 'imperial'
      : 'metric'

  if (heightMeasurementSystem !== weightMeasurementSystem) {
    console.error('Unable to calculate BMI: different measurement systems of weight and height')
    return
  }

  const weight = +(value || weightData.value)

  if (heightMeasurementSystem === 'metric') {
    return (weight / Math.pow(parseInt(heightData.quantity.cm), 2)) * 10000
  } else {
    return (
      (weight * 703) /
      Math.pow(parseInt(heightData.quantity.feet) * 12 + parseInt(heightData.quantity.inches), 2)
    )
  }
}

type TRecommendedWeight = {
  unit: WeightParameters
  min: number
  max: number
}

export const getRecommendedWeight = (getItem: TGetItemFn): TRecommendedWeight => {
  const heightData = getItem(PageName.height) || ''
  const heightDataObject = (heightData && JSON.parse(heightData)) || ''

  const weightData = getItem(PageName.weight) || ''
  const weightDataObject = (heightData && JSON.parse(weightData)) || ''

  if (heightDataObject?.type === 'metric') {
    const cm = heightDataObject?.quantity?.cm
    const minRecommendedWeight = Math.round(
      (BMI_REFERENCE_VALUE * Math.pow(parseInt(cm), 2)) / 10000
    )
    const calcValue = Math.round((BMI_REFERENCE_VALUE_IMPERIAL * Math.pow(parseInt(cm), 2)) / 10000)
    let maxRecommendedWeight: number = calcValue
    if (calcValue >= +weightDataObject.value) {
      maxRecommendedWeight = Math.max(minRecommendedWeight, +weightDataObject.value - 1)
    }
    return {
      unit: WeightParameters.KG,
      min: minRecommendedWeight,
      max: maxRecommendedWeight,
    }
  }
  if (heightDataObject?.type === 'imperial') {
    const feet = heightDataObject?.quantity?.feet
    const inc = heightDataObject?.quantity?.inches
    const minRecommendedWeight = Math.round(
      (BMI_REFERENCE_VALUE * Math.pow(parseInt(feet) * 12 + parseInt(inc), 2)) / 703
    )
    const calcValue = Math.round(
      (BMI_REFERENCE_VALUE_IMPERIAL * Math.pow(parseInt(feet) * 12 + parseInt(inc), 2)) / 703
    )
    let maxRecommendedWeight: number = calcValue
    if (calcValue >= +weightDataObject.value) {
      maxRecommendedWeight = Math.max(minRecommendedWeight, +weightDataObject.value - 1)
    }
    return {
      unit: WeightParameters.LBS,
      min: minRecommendedWeight,
      max: maxRecommendedWeight,
    }
  }

  throw Error('unknown measurement system')
}

export const validWeightValue = (
  unit: string,
  value: string,
  pageName: string,
  getItem: TGetItemFn,
  validateBMIRefValue = true
) => {
  const prevPageData = getItem(PageName.height) || ''
  const convertPrevPageDataToObject = (prevPageData && JSON.parse(prevPageData)) || ''

  const currentUnitType = unit === WeightParameters.LBS ? 'imperial' : 'metric'
  const intVal = parseInt(value)

  if (validateBMIRefValue && pageName === PageName.weight) {
    if (currentUnitType !== convertPrevPageDataToObject?.type) return null

    if (currentUnitType === 'metric') {
      const BMI = (intVal / Math.pow(parseInt(convertPrevPageDataToObject.quantity.cm), 2)) * 10000

      if (BMI <= BMI_REFERENCE_VALUE) {
        return ERROR_MESSAGE
      }
    } else {
      const BMI =
        (intVal * 703) /
        Math.pow(
          parseInt(convertPrevPageDataToObject.quantity.feet) * 12 +
            parseInt(convertPrevPageDataToObject.quantity.inches),
          2
        )
      if (BMI <= BMI_REFERENCE_VALUE) {
        return ERROR_MESSAGE
      }
    }
  }

  if (pageName === PageName.weightGoal) {
    const recommendedWeight = getRecommendedWeight(getItem)
    if (unit !== recommendedWeight.unit) return null

    const prevWeightData = JSON.parse(getItem(PageName.weight) || '')

    if (prevWeightData?.value && intVal >= prevWeightData.value) {
      return `Value must be less than ${prevWeightData?.value} ${prevWeightData?.measurementSystem}"`
    }
    return null
  }
}

export const getWeightToGoalTitle = (
  unit: string,
  currentWeight: number,
  goalWeight: number,
  eventDate: string,
  goalEvent: string
) => {
  const units = unit === 'metric' ? ' kg' : ' lbs'

  if (eventDate !== 'No' && goalEvent !== 'No') {
    const dateNow = new Date()
    const dateDiff = Math.ceil((Date.parse(eventDate) - Number(dateNow)) / (60 * 60 * 24 * 1000))
    const loseByWeak =
      unit === 'metric' ? Math.floor((dateDiff / 7) * 0.7) : Math.floor((dateDiff / 7) * 1.543)
    if (currentWeight - loseByWeak > goalWeight) {
      return `And lose <span>~${loseByWeak} ${units}</span> by the <span>${goalEvent.toLowerCase()}</span>`
    }

    return `And reach your goal by the <span>${goalEvent}</span>`
  }
}

export const getEstimatedMonth = (unit: string, currentWeight: number, goalWeight: number) => {
  return unit === 'metric'
    ? Math.ceil((currentWeight - goalWeight) / 3)
    : Math.ceil((currentWeight - goalWeight) / 6.614)
}

export const getGoalWeightByDate = (unit: string, currentWeight: number, goalWeight: number) => {
  const today = new Date()

  const estimatedMonth = getEstimatedMonth(unit, currentWeight, goalWeight)

  const goalMonth = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
    today.setMonth(today.getMonth() + estimatedMonth)
  )
  const goalYear = today.getFullYear()
  return `${goalMonth} ${goalYear}`
}
